import { format } from 'date-fns';
import {
  LoaderIcon,
  OctagonXIcon,
  SendIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSendOccupationReportReminder } from '@/hooks/mutations/use-send-occupation-report-reminder';
import { useGenerateOccupationReport } from '@/hooks/queries/use-generate-occupation-report';
import { popModal } from '..';

const selectedViewModel = ({
  modalApi,
  initialShow,
}: {
  modalApi: OccupationReportModalApi;
  initialShow: boolean;
}) => {
  const [show, setShow] = useState<boolean>(initialShow);
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  const closeModal = () => {
    popModal('OccupationReportModal');
    setShow(false);
    modalApi.onClose && modalApi.onClose();
  };

  return {
    api: {
      close: closeModal,
      setOpenPopover,
    },
    data: {
      show,
      openPopover,
    },
  };
};

export type OccupationReportModalViewModel = ReturnType<
  typeof selectedViewModel
>;

export type OccupationReportModalApi = {
  onClose?: () => void;
};

export type OccupationReportModalProps = {
  show?: boolean;
  officeId: string;
  officeName: string;
  dateRange: {
    from: Date;
    to: Date;
  };
} & OccupationReportModalApi;

export const OccupationReportModal = forwardRef(
  (
    {
      show = true,
      officeId,
      officeName,
      dateRange,
      onClose,
    }: OccupationReportModalProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const viewModel = selectedViewModel({
      initialShow: show,
      modalApi: {
        onClose,
      },
    });

    useImperativeHandle(ref, () => {
      return viewModel;
    }, []);

    // Fetch occupation report data from API
    const {
      data: occupationData,
      isLoading,
      error,
    } = useGenerateOccupationReport({
      officeId,
      from: format(dateRange.from, 'yyyy-MM-dd'),
      to: format(dateRange.to, 'yyyy-MM-dd'),
    });

    // Mutation for sending reminder emails
    const sendReminderMutation = useSendOccupationReportReminder();

    const errorsData = occupationData?.data?.errors || [];
    const warningsData = occupationData?.data?.warnings || [];

    const formatDateRange = () => {
      const fromFormatted = format(dateRange.from, 'dd/MM/yyyy');
      const toFormatted = format(dateRange.to, 'dd/MM/yyyy');
      return `${fromFormatted} - ${toFormatted}`;
    };

    const formatPeriod = (period: string) => {
      switch (period) {
        case 'morning':
          return 'matin';
        case 'afternoon':
          return 'après-midi';
        case 'allday':
          return 'toute la journée';
        case 'night':
          return 'nuit';
        default:
          return period;
      }
    };

    const formatDate = (dateString: string) => {
      // Date comes in YYYY-MM-DD format, convert to DD/MM/YYYY
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy');
    };

    const handleGlobalSendConfirmation = () => {
      // Close the popover
      viewModel.api.setOpenPopover(null);

      // Send reminder emails
      sendReminderMutation.mutate(
        {
          officeId,
          from: format(dateRange.from, 'yyyy-MM-dd'),
          to: format(dateRange.to, 'yyyy-MM-dd'),
        },
        {
          onSuccess: () => {
            // Show success toast
            toast.success(t('occupation-report.reminder.success.title'), {
              description: t('occupation-report.reminder.success.description', {
                officeName,
              }),
            });
          },
          onError: (error) => {
            // Show error toast
            toast.error(t('occupation-report.reminder.error.title'), {
              description: t('occupation-report.reminder.error.description'),
            });
            console.error('Failed to send reminder:', error);
          },
        },
      );
    };

    const handleCancelSend = () => {
      // Just close the popover
      viewModel.api.setOpenPopover(null);
    };

    return (
      <Dialog
        open={viewModel.data.show}
        onOpenChange={(open) => {
          if (!open) viewModel.api.close();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('occupation-report.title')}</DialogTitle>
            <DialogDescription>
              {officeName} | {formatDateRange()}
            </DialogDescription>
          </DialogHeader>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center p-8">
              <LoaderIcon className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                {t('occupation-report.loading')}
              </span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
              <div className="flex items-start gap-3">
                <TriangleAlertIcon className="h-4 w-4 text-destructive mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-destructive mb-1">
                    {t('occupation-report.error.title')}
                  </h4>
                  <p className="text-sm text-destructive/90">
                    {t('occupation-report.error.description')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && (
            <div className="space-y-4">
              <Tabs
                defaultValue="errors"
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger
                    value="errors"
                    className="relative"
                  >
                    {t('occupation-report.tabs.errors')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="warnings"
                    className="relative"
                  >
                    {t('occupation-report.tabs.warnings')}
                    <Badge
                      variant="destructive"
                      className="ml-2 h-5 min-w-5 text-xs"
                    >
                      {warningsData.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="errors"
                  className="space-y-4"
                >
                  {/* Alert Component (custom since shadcn/ui alert is not available) */}
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
                    <div className="flex items-start gap-3">
                      <TriangleAlertIcon className="h-4 w-4 text-destructive mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-destructive mb-1">
                          {t('occupation-report.errors.alert.title')}
                        </h4>
                        <p className="text-sm text-destructive/90">
                          {t('occupation-report.errors.alert.description')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            {t('occupation-report.table.date')}
                          </TableHead>
                          <TableHead>
                            {t('occupation-report.table.period')}
                          </TableHead>
                          <TableHead>
                            {t('occupation-report.table.simultaneousSlots')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {errorsData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {formatDate(row.date)}
                            </TableCell>
                            <TableCell>{formatPeriod(row.period)}</TableCell>
                            <TableCell>
                              <Badge variant="destructive">
                                {row.simultaneousBookings}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                <TabsContent
                  value="warnings"
                  className="space-y-4"
                >
                  {/* Alert Component for warnings */}
                  <div className="rounded-lg border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <TriangleAlertIcon className="h-4 w-4 text-foreground mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-foreground mb-1">
                          {t('occupation-report.warnings.alert.title')}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {t('occupation-report.warnings.alert.description')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Table for warnings */}
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            {t('occupation-report.table.date')}
                          </TableHead>
                          <TableHead>
                            {t('occupation-report.table.period')}
                          </TableHead>
                          <TableHead>
                            {t('occupation-report.table.simultaneousSlots')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {warningsData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {formatDate(row.date)}
                            </TableCell>
                            <TableCell>{formatPeriod(row.period)}</TableCell>
                            <TableCell>
                              {row.simultaneousBookings === 0 ? (
                                <Badge variant="default">
                                  {row.simultaneousBookings}
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  {row.simultaneousBookings}
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Global Send Reminder Button */}
                  <div className="flex justify-end">
                    <Popover
                      open={viewModel.data.openPopover === 'global-send'}
                      onOpenChange={(open) =>
                        viewModel.api.setOpenPopover(
                          open ? 'global-send' : null,
                        )
                      }
                    >
                      <PopoverTrigger asChild>
                        <Button className="gap-2">
                          <SendIcon className="h-4 w-4" />
                          {t('occupation-report.reminder.button')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-80 p-4"
                        align="end"
                        sideOffset={5}
                      >
                        <div className="space-y-4">
                          <p className="text-sm text-foreground leading-5">
                            {t('occupation-report.reminder.confirmation', {
                              officeName,
                            })}
                          </p>
                          <div className="flex gap-2.5">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 gap-2"
                              onClick={handleCancelSend}
                            >
                              <OctagonXIcon className="h-4 w-4" />
                              {t('btn.cancel')}
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 gap-2"
                              onClick={() => handleGlobalSendConfirmation()}
                              disabled={sendReminderMutation.isPending}
                            >
                              {sendReminderMutation.isPending ? (
                                <LoaderIcon className="h-4 w-4 animate-spin" />
                              ) : (
                                <SendIcon className="h-4 w-4" />
                              )}
                              {sendReminderMutation.isPending
                                ? t('occupation-report.reminder.sending')
                                : t('occupation-report.reminder.send')}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={viewModel.api.close}
            >
              {t('btn.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);
